import { useState } from "react";
import { ReasonCancelled, isReasonCancelled, useReasonDialog } from "@/shared/ui/ReasonDialog";   // REASON-1
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Plus, Users } from "lucide-react";
import { pricingApi } from "@/services/pricing";
import { partnersApi } from "@/services/b2b";
import { Can } from "@/auth/permissions";
import { PageShell, Pill, LoadingState, EmptyState } from "@/shared/ui/PageHeader";

/**
 * W4 · 客户专属定价 list + 加/删
 *  - select B2B 客户 + 设折扣 %（负数 = 折扣，正数 = 加价）
 *  - 与渠道 markup / 节假日加价 组合（公式预览见 PricingCenter）
 */
export function CustomerOverridesPage() {
  const qc = useQueryClient();
  const overrides = useQuery({
    queryKey: ["pricing", "customer-overrides"],
    queryFn: () => pricingApi.listCustomerOverrides(),
  });
  const partners = useQuery({
    queryKey: ["b2b", "partners"],
    queryFn: () => partnersApi.list(),
  });

  const [partnerId, setPartnerId] = useState("");
  const [discountPct, setDiscountPct] = useState(-10);
  const [note, setNote] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");

  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();   // REASON-1
  const create = useMutation({
    mutationFn: async () => {
      const r = await askReason({
        scene: "price_change", title: "设置客户专属折扣",
        changes: [{ label: "客户专属折扣 / 加价", before: "无", after: `${discountPct > 0 ? "+" : ""}${discountPct}%` }], confirmText: "确认并保存",
      });
      if (!r) throw new ReasonCancelled();
      return pricingApi.createCustomerOverride({
        partner_id: partnerId,
        discount_pct: discountPct,
        note: note || undefined,
        effective_from: effectiveFrom || undefined,
        effective_to: effectiveTo || undefined,
        ...r,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pricing", "customer-overrides"] });
      setNote("");
    },
    onError: (e) => { if (!isReasonCancelled(e)) alert((e as Error).message || "保存失败"); },
  });

  const remove = useMutation({
    mutationFn: (id: string) => pricingApi.deleteCustomerOverride(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing", "customer-overrides"] }),
    onError: (e) => alert((e as Error).message || "删除失败"),
  });

  return (
    <PageShell
      title="客户专属定价"
      subtitle="B2B 客户专属折扣 / 加价 · 覆盖渠道 markup 默认"
    >
      <Can perm="pricing.policies.update">
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <Users className="h-4 w-4 text-blue-600" />
          <h2 className="text-base font-semibold text-slate-900">新建客户专属</h2>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
          <Field label="客户" span={2}>
            <select
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              className="h-9 w-full rounded-lg border border-slate-200 px-2 text-[13px]"
            >
              <option value="">选择客户</option>
              {(partners.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="折扣 % (负=折扣，正=加价)">
            <input
              type="number"
              step="0.5"
              value={discountPct}
              onChange={(e) => setDiscountPct(Number(e.target.value))}
              className="h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px]"
            />
          </Field>
          <Field label="生效日">
            <input
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              className="h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px]"
            />
          </Field>
          <Field label="到期日（留空=永久）">
            <input
              type="date"
              value={effectiveTo}
              onChange={(e) => setEffectiveTo(e.target.value)}
              className="h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px]"
            />
          </Field>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="md:col-span-3">
            <Field label="备注">
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="h-9 w-full rounded-lg border border-slate-200 px-3 text-[13px]"
                placeholder="如：旺季合作 5% 折扣"
              />
            </Field>
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => create.mutate()}
              disabled={!partnerId || create.isPending}
              className="flex h-9 w-full items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" /> 添加
            </button>
          </div>
        </div>

        {create.isError && (
          <div className="mt-2 text-[12px] text-rose-600">
            创建失败：{(create.error as Error).message}
          </div>
        )}
      </div>
      </Can>

      {overrides.isLoading ? (
        <LoadingState />
      ) : overrides.isError ? (
        <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500"><span>客户专属定价加载失败</span><button onClick={() => overrides.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button></div>
      ) : (overrides.data ?? []).length === 0 ? (
        <EmptyState message="暂无客户专属定价 · 上方新建" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">客户</th>
                <th className="px-4 py-3 text-right">折扣 / 加价</th>
                <th className="px-4 py-3 text-left">生效区间</th>
                <th className="px-4 py-3 text-left">备注</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {(overrides.data ?? []).map((o) => (
                <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-900">{o.partner_name}</td>
                  <td className="px-4 py-3 text-right">
                    <Pill tone={o.discount_pct < 0 ? "success" : "warning"}>
                      {o.discount_pct > 0 ? "+" : ""}
                      {o.discount_pct}%
                    </Pill>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {o.effective_from} → {o.effective_to ?? "永久"}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{o.note ?? "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Can perm="pricing.policies.update"><button
                      type="button"
                      disabled={remove.isPending}
                      onClick={() => { if (confirm(`删除「${o.partner_name}」的专属定价？`)) remove.mutate(o.id); }}
                      className="flex h-7 items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2 text-[11px] font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                    >
                      <Trash2 className="h-3 w-3" /> 删除
                    </button></Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    {reasonDialog}
    </PageShell>
  );
}

function Field({
  label,
  children,
  span,
}: {
  label: string;
  children: React.ReactNode;
  span?: 2 | 3 | 4;
}) {
  const spanCls = span === 2 ? "md:col-span-2" : span === 3 ? "md:col-span-3" : span === 4 ? "md:col-span-4" : "";
  return (
    <label className={"flex flex-col gap-1 " + spanCls}>
      <span className="text-[11px] font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}
